import * as React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { TenorGif } from '../../types'
import Icon from '../Icons/Icon'
import Loader from '../../widget/components/Loader/Loader'
import { GifsController } from '../../controllers'

const Container = styled.div`
  position: relative;
  background-color: rgb(255, 255, 255);
  border: 1px solid rgb(245, 245, 245);
  border-radius: 12px;
  box-shadow: rgba(9, 14, 21, 0.16) 0px 0px 4px 0px;
  width: 330px;
  height: 260px;
  overflow: hidden;
  z-index: 2147483001;
  margin: 0px 30px;
  padding: 12px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 12px;
  border-bottom: 1px solid rgb(245, 245, 245);
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    position: absolute;
    left: 4px;
    width: 16px;
    height: 16px;
  }
`

const Search = styled.input`
  width: 100%;
  font-weight: normal;
  font-size: 14px;
  color: rgb(20, 22, 26);
  padding: 10px 0px 10px 25px !important;
  height: 40px;
  width: 100%;
  border: none;
  outline: none;
`

const ItemsContainer = styled.div`
  flex: 1;
  position: relative;
`

const Items = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const Item = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  height: 150px;
  cursor: pointer;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
  border-radius: 10px;
`

const LoaderContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`


interface IProps {
  onGifSelect: (gif: any) => void
}

interface IState {
  didInitialLoad: boolean
  endReached: boolean
  search: string
  next: string
  gifs: TenorGif[]
}

const GiphyPopover = (props: IProps) => {
  const { t } = useTranslation()
  const { onGifSelect } = props

  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    search: '',
    next: null,
    endReached: false,
    gifs: []
  })
  const { didInitialLoad, search, gifs, next, endReached } = state
  const wrapper = React.useRef<HTMLDivElement>(null)

  const fetchGifs = async (search?: string, next?: string) => {
    if (endReached) return

    try {
      const { next: futureNext, results } = await GifsController.getGifs(search, next);

      setState({
        ...state,
        didInitialLoad: true,
        next: futureNext,
        endReached: futureNext === next,
        gifs: next ? [...gifs, ...results] : [...results]
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setState(prevState => ({
      ...prevState,
      didInitialLoad: false,
      endReached: false,
      search: value,
      gifs: [],
      next: null,
    }));

    debouncedFetchGifs(value)
  }

  const onEndReached = () => {
    const { scrollTop, scrollHeight, clientHeight } = wrapper.current;
    const scrollContainerEndReached = scrollTop + clientHeight >= scrollHeight - 300;

    if (
      didInitialLoad &&
      scrollContainerEndReached &&
      next &&
      !endReached
    ) {
      debouncedFetchGifs(search, next)
    }
  }

  React.useEffect(() => {
    fetchGifs().catch(console.error);
  }, [])

  React.useEffect(() => {
    if (wrapper?.current) {
      wrapper.current.addEventListener('scroll', onEndReached)

      return () => {
        wrapper.current?.removeEventListener('scroll', onEndReached);
      }
    }
  }, [wrapper?.current, didInitialLoad, next, endReached])

  const debouncedFetchGifs = useDebouncedCallback(
    async (searchTerm: string, next?: string) => {
      fetchGifs(searchTerm, next).catch(console.error);
    },
    500
  );

  const debouncedEndReached = useDebouncedCallback(
    () => {
      onEndReached();
    },
    200, {
    leading: true,
  });


  return (
    <Container>
      <Wrapper>
        <SearchContainer>
          <Icon icon='messenger-search' />
          <Search
            type='text'
            placeholder={t('GiphyPopover::Search GIFS...')}
            value={search}
            onChange={onSearchChange}
          />
        </SearchContainer>
        {!didInitialLoad && <LoaderContainer>
          <Loader />
        </LoaderContainer>}
        {didInitialLoad && <>
          <ItemsContainer>
            <Items ref={wrapper}>
              {gifs.map((gif, index) => {
                const mediaFormatUrl = gif.media_formats?.gif?.url
                return (
                  <Item
                    key={`${gif.id}-${index}`}
                    style={{ backgroundImage: `url('${mediaFormatUrl}')` }}
                    onClick={() => onGifSelect(mediaFormatUrl)}
                  />
                )
              })}
            </Items>
          </ItemsContainer>
        </>}
      </Wrapper>
    </Container>
  )
}

export default GiphyPopover